import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, ButtonProps, Popover, PopoverProps } from '@mui/material';
import { ReactNode, forwardRef, useId, useImperativeHandle, useState } from 'react';

export type PopoutButtonProps = {
    buttonContent?: ReactNode;
    buttonProps?: ButtonProps;
    children: ReactNode;
    showStateChevron?: boolean;
} & Omit<PopoverProps, 'id' | 'open' | 'anchorEl' | 'onClose'>;

export type PopoutButtonHandle = { close: () => void };

export const PopoutButton = forwardRef<PopoutButtonHandle, PopoutButtonProps>(function PopoutButton(
    { buttonContent, buttonProps, showStateChevron, children, ...props },
    ref
) {
    useImperativeHandle(
        ref,
        () => ({
            close() {
                onClose();
            },
        }),
        []
    );
    const id = useId();

    const [anchorEl, setAnchorEl] = useState<Element | null>(null);

    const onClick = (event: React.MouseEvent<Element>) => {
        if (buttonProps?.disabled) {
            return;
        }

        if (!anchorEl) {
            setAnchorEl(event.currentTarget);
        } else {
            onClose();
        }
    };

    const onClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    return (
        <>
            <Button {...buttonProps} onClick={onClick}>
                {buttonContent}
                {showStateChevron &&
                    (open ? (
                        <ExpandLessIcon sx={{ position: 'relative', left: 8 }} />
                    ) : (
                        <ExpandMoreIcon sx={{ position: 'relative', left: 8 }} />
                    ))}
            </Button>
            <Popover
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                {...props}
            >
                {children}
            </Popover>
        </>
    );
});
